///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";

import CloseIcon from "../../icons/CloseIcon";
import messages from "../../../helpers/messages";
import DATImage from "../../../images/DATImage.jpg";
import { fetchDatRates } from "../../../slices/analyticsSlice";
import PanelStateIndicator from "../PanelStateIndicator";
import Arrow from "../../icons/Arrow";
import { FormattedMessage } from "react-intl";
import { filterEquipmentTypes } from "../../../helpers/types";

export const DATRateViewDrawer = ({ open, onClose }) => {
  const {
    analytics: {
      datRate: { isFetching, isFailure, datRates },
      filters: { origin, destination, equipments }
    }
  } = useSelector(state => state);
  const dispatch = useDispatch();

  let contractRate = datRates?.rateResponses.find(
    x => x.request.rateType === "CONTRACT"
  );
  let spotRate = datRates?.rateResponses.find(
    x => x.request.rateType === "SPOT"
  );
  let eq = filterEquipmentTypes.find(x => x.label === "V").label;

  if (equipments?.length === 1) {
    if (
      equipments[0].label === "V" ||
      equipments[0].label === "R" ||
      equipments[0].label === "F"
    ) {
      eq = equipments[0].label;
    }
  }

  const hasNoRates =
    contractRate?.response.errors.length > 0 &&
    spotRate?.response.errors.length > 0;

  const formatNumber = number => {
    return number.toLocaleString("en-US");
  };

  const LocationInfo = () => {
    return (
      <div className="pt-24">
        <div className="text-center gamma-heading weight--light">
          <span className="pr-16" data-testid="origin">
            {origin?.cityName}, {origin?.stateCode}
          </span>
          <Arrow height={16} width={16} rotateDegree={-90} />
          <span className="pl-16" data-testid="destination">
            {destination?.cityName}, {destination?.stateCode}
          </span>
        </div>
        {!hasNoRates &&
          (contractRate?.response.rate?.mileage ||
            spotRate?.response.rate?.mileage) && (
            <div className="text-center pt-8 grey-color">
              <span data-testid="mileage">
                <FormattedMessage
                  {...messages.mileage}
                  values={{
                    mileage:
                      contractRate?.response.rate?.mileage ??
                      spotRate?.response.rate?.mileage
                  }}
                />
              </span>
            </div>
          )}
        <div className="text-center pt-4 grey-color" data-testid="equipment">
          <span className="weight--semibold">
            {messages.equipmentType.defaultMessage}:{" "}
          </span>
          <span>{eq}</span>
        </div>
      </div>
    );
  };

  const BrokerToCarrierRateTypeInfo = ({
    brokerToCarrierHeader,
    rateResponse
  }) => {
    return (
      <>
        <div className="pt-16">
          <div
            className="grid__cell t70 font-size-14 weight--semibold alpha-body-copy"
            data-testid="brokerToCarrierHeader"
          >
            {brokerToCarrierHeader}
          </div>
          <div className="grid__cell t30 text-right" data-testid="rate">
            <span className="block delta-heading default-font-family weight--semibold">
              {`$${formatNumber(
                rateResponse?.response.rate.perTrip.rateUsd +
                  (rateResponse?.response.rate.averageFuelSurchargePerTripUsd ??
                    0)
              )}`}
            </span>
          </div>
        </div>

        <div className="pt-8">
          <div className="grid__cell t60" data-testid="fuel">
            <span>{`(Fuel: $${formatNumber(
              rateResponse?.response.rate.averageFuelSurchargePerTripUsd
            )} this week)`}</span>
          </div>
          <div className="grid__cell t40 text-right" data-testid="rate-range">
            {`LNH ($${formatNumber(
              rateResponse?.response.rate.perTrip.lowUsd
            )} - $${formatNumber(
              rateResponse?.response.rate.perTrip.highUsd
            )})`}
          </div>
        </div>

        <div className="pt-8">
          <div className="grid__cell" data-testid="reports-companies">
            <FormattedMessage
              {...messages.reportsAndCompaniesCount}
              values={{
                reports: rateResponse?.response.rate.reports,
                companies: rateResponse?.response.rate.companies
              }}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={375}
      className="drawer"
      lockBackgroundScroll={true}
    >
      <div className="pt-12 pb-12 flex items-center header">
        <CloseIcon
          height="16px"
          width="16px"
          className="cursor-pointer ml-16 mr-16"
          onClick={onClose}
        />
        <img src={DATImage} alt={messages.dat.defaultMessage} />
        <span className="pl-8">{messages.rateView.defaultMessage}</span>
      </div>

      {(isFetching || isFailure) && (
        <div className="box panel">
          <PanelStateIndicator
            iconSize="32px"
            loading={isFetching}
            error={!isFetching && isFailure}
            handleRefresh={() => dispatch(fetchDatRates())}
            errorMessage={
              messages.sorrySomethingWentWrongPleaseTryAgain.defaultMessage
            }
          />
        </div>
      )}

      {!isFetching && !isFailure && !hasNoRates && (
        <>
          <LocationInfo />

          <div className="pt-24 grid section-grid p-16">
            <div>
              <div
                className="grid__cell t70 font-size-14 weight--semibold alpha-body-copy"
                data-testid="broker-to-carrier-timeframe"
              >
                {messages.brokerToCarrierTimeframe.defaultMessage}
              </div>
              <div className="grid__cell t30 text-right">
                <span
                  className="block capitalize"
                  data-testid="specificTimeFrame"
                >
                  {contractRate?.request.targetEscalation.specificTimeFrame
                    .toLowerCase()
                    .split("_")
                    .join(" ")}
                </span>
                <span
                  className="block grey-color pt-4 capitalize"
                  data-testid="specificAreaType"
                >
                  {contractRate?.request.targetEscalation.specificAreaType
                    .toLowerCase()
                    .split("_")
                    .join(" ")}
                </span>
              </div>
            </div>

            {spotRate && spotRate?.response.errors.length === 0 && (
              <BrokerToCarrierRateTypeInfo
                brokerToCarrierHeader={
                  messages.brokerToCarrierSpot.defaultMessage
                }
                rateResponse={spotRate}
              />
            )}

            {contractRate && contractRate?.response.errors.length === 0 && (
              <BrokerToCarrierRateTypeInfo
                brokerToCarrierHeader={
                  messages.shipperToCarrierContract.defaultMessage
                }
                rateResponse={contractRate}
              />
            )}

            <div className="pt-44">
              <div className="grid__cell disclaimer" data-testid="disclaimer">
                {messages.datDisclaimer.defaultMessage}
              </div>
            </div>
          </div>
        </>
      )}

      {!isFetching && !isFailure && hasNoRates && (
        <>
          <LocationInfo />

          <div className="box panel">
            <div className="box-content disclaimer pl-44 pr-44 text-center">
              <span className="no-datRateView" data-testid="no-datRateView">
                {messages.unableToRetrieveData.defaultMessage}
              </span>
              <p
                className="pt-12 text-left light-grey"
                data-testid="no-datRateView-message"
              >
                {messages.noDatRateViewMessage.defaultMessage}
                <a className="light-blue" href="https://www.dat.com/" target="_blank" rel="noreferrer">{messages.datWebsite.defaultMessage}</a>.
              </p>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};
